/**
 * spUi.jsx - the Spare Parts tab's small shared pieces: the price-age chip,
 * a message line, a column header that sorts, and the table cell styles.
 * Inputs and buttons are the quote builder's (components/quotes/quoteUi.jsx),
 * so the tab looks like the rest of GlobalOps.
 */
import { Icon } from '../ui/Icon';
import { BAND_STYLE, fmtDay } from './sparePartsRules';
import { GREEN, LINE, MUTED } from '../quotes/quoteUi';

export { GREEN, LINE, MUTED };

/**
 * The price chip: the day the price was checked and its colour - GREEN 0-3
 * months, YELLOW 4-8, RED 9+ with a warning "Get prices checked", grey "No
 * price yet". `age` is the server's price_age ({ band, months, label }).
 */
export function PriceChip({ age, checkedAt, compact = false }) {
  const band = (age && age.band) || 'none';
  const s = BAND_STYLE[band];
  const day = checkedAt ? fmtDay(checkedAt) : null;
  const title =
    band === 'none'
      ? s.hint
      : `${age.label}${day ? ` - checked ${day}` : ''}${age.months !== null && age.months !== undefined ? ` (${age.months} month${age.months === 1 ? '' : 's'} ago)` : ''}`;
  return (
    <span
      data-testid="price-chip"
      data-band={band}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {band === 'red' ? (
        <span aria-hidden="true" style={{ display: 'inline-flex' }}>
          <Icon name="alert" size={12} color={s.dot} />
        </span>
      ) : (
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 999, background: s.dot }} />
      )}
      {band === 'none' ? s.label : day || 'Date unknown'}
      {band === 'red' && !compact && <span>· Get prices checked</span>}
    </span>
  );
}

/** A line saying what happened: green for done, red for a refusal. */
export function Msg({ msg }) {
  if (!msg) return null;
  const bad = msg.kind === 'error';
  return (
    <div
      role={bad ? 'alert' : 'status'}
      style={{
        margin: '8px 0',
        padding: '7px 12px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        background: bad ? '#FFEBEE' : '#E8F5E9',
        color: bad ? '#B71C1C' : GREEN,
      }}
    >
      {msg.text}
    </div>
  );
}

export const th = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '6px 8px',
  borderBottom: `1px solid ${LINE}`,
  whiteSpace: 'nowrap',
  background: '#FAFBF7',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
export const td = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #F1F3EE', verticalAlign: 'middle' };
export const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/** A header cell that sorts its column; aria-sort says which way. */
export function SortTh({ label, col, sort, onSort, right, title }) {
  const on = sort.key === col;
  return (
    <th
      style={{ ...th, textAlign: right ? 'right' : 'left' }}
      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={title}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        style={{
          all: 'unset',
          cursor: 'pointer',
          color: on ? GREEN : MUTED,
          fontWeight: 700,
        }}
      >
        {label}
        {on ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
}

/** A label over a value, read-only. */
export function Fact({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 12.5, color: '#1F2328', marginTop: 2, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}
